import { AxiosError } from "axios";
import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import adminAxios from "@/lib/axios";

export interface IFaqCategory {
  id?: number;
  faq_id?: number;
  name: string;
  text_color?: string;
  background_color?: string;
  order_index: number;
  status?: boolean;
  is_active?: boolean;
  faqs?: IFaqItem[];
}

export interface IFaqItem {
  id?: number;
  category_id: number;
  question: string;
  question_text_color?: string;
  answer: string;
  answer_text_color?: string;
  /** FAQ variant 4 — per-card background color */
  background_color?: string;
  priority: number;
  status?: boolean;
  is_active?: boolean;
}

export interface IFaq {
  id?: number;
  page_id?: number;
  component_id?: number;
  title?: string;
  variant?: string;
  status?: boolean;
  enable_animation?: boolean | number;
  categories?: IFaqCategory[];
}

// Category API methods
export async function createFaqCategory(payload: Partial<IFaqCategory>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.FAQ.CATEGORY.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateFaqCategory(id: number, payload: Partial<IFaqCategory>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.FAQ.CATEGORY.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteFaqCategory(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.FAQ.CATEGORY.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getFaqCategoriesByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.FAQ.CATEGORY.GET_BY_COMPONENT_ID(componentId)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// FAQ API methods
export async function createFaq(payload: Partial<IFaq>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.FAQ.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateFaq(id: number, payload: Partial<IFaq>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.FAQ.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getFaqById(id: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.FAQ.GET_BY_ID(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getFaqByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.FAQ.GET_BY_COMPONENT_ID(componentId)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteFaq(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.FAQ.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// FAQ Item API methods (for items within categories)
export async function createFaqItem(payload: Partial<IFaqItem>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.FAQ.FAQ.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateFaqItem(id: number, payload: Partial<IFaqItem>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.FAQ.FAQ.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getFaqItemsByCategoryId(categoryId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.FAQ.FAQ.GET_BY_CATEGORY_ID(categoryId)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteFaqItem(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.FAQ.FAQ.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function cloneFaqWithCategories(
  sourceFaqId: number,
  targetPageComponentId: number,
  targetFaqId?: number | null
) {
  try {
    const response = await adminAxios.post(ADMIN_API_ENDPOINTS.FAQ.CLONE,
      {
        source_faq_id: sourceFaqId,
        target_page_component_id: targetPageComponentId,
        target_faq_id: targetFaqId || null,
      }
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}